/**
 * Tenant Setup Console (super-admin only) - SPEC tenant-config-platform R3.
 *
 * Configures ANY tenant end-to-end from the UI: identity / module config,
 * branding, tracking ids (Meta pixel + GA4), integrations, KB pointer + agent
 * config, CORS origins, Cal.com config, the encrypted-secrets panel (R2), the
 * go-live toggles (send_enabled / agent_paused), and a config-change rebuild
 * dispatch. GENERIC for any tenant - no per-tenant UI code.
 *
 * It WIRES the existing /api/admin/super/* writes (api/admin.py) + the R2 secrets
 * API + R1's public-config read-back; it does NOT duplicate backend logic.
 *
 * Rule 0: only super-admins reach it (gated below); protected tenants
 * (dvw4ea / buildgravity) are refused server-side BEFORE any write - the UI
 * surfaces that refusal. This console does NOT default any tenant to sending:
 * the go-live toggles default safe and require an explicit operator action.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Settings, ArrowLeft, Save, RefreshCw, ShieldAlert, KeyRound, Rocket,
  Power, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getTenantSetupConfig,
  getTenantBranding,
  saveTenantBranding,
  saveTenantConfig,
  saveTenantIntegrations,
  saveTenantOrigins,
  saveTenantCalConfig,
  getSetterConfig,
  saveSetterConfig,
  listTenantSecrets,
  setTenantSecret,
  dispatchTenantRebuild,
  getPublicTenantConfig,
  type SetterConfigState,
  type SecretStatus,
  type PublicTenantConfig,
} from '@/lib/api/agency';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const PROTECTED_TENANTS = ['dvw4ea', 'buildgravity'];

/** Read a nested string off an object path without throwing (prefill helper). */
function pick(obj: unknown, ...path: string[]): string {
  let cur: unknown = obj;
  for (const k of path) {
    if (!cur || typeof cur !== 'object') return '';
    cur = (cur as Record<string, unknown>)[k];
  }
  return cur == null ? '' : String(cur);
}

// Firestore merge treats an empty map as replacement. Never let an editor
// with omitted credentials clear a hidden secret-only map on a round trip.
function integrationPatch(settings: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(settings).flatMap(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = integrationPatch(value as Record<string, unknown>);
      return Object.keys(nested).length ? [[key, nested]] : [];
    }
    return [[key, value]];
  }));
}

export function TenantSetupConsole() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const isProtected = PROTECTED_TENANTS.includes(tenantId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // identity / modules
  const [name, setName] = useState('');
  const [modulesText, setModulesText] = useState('');

  // branding
  const [displayName, setDisplayName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [accentColor, setAccentColor] = useState('');

  // tracking ids (written via integrations: integrations.meta.pixel_id /
  // integrations.ga4.measurement_id - the public-config builder reads both)
  const [pixelId, setPixelId] = useState('');
  const [ga4Id, setGa4Id] = useState('');

  // integrations (free-form JSON for the full integrations block)
  const [integrationsText, setIntegrationsText] = useState('');

  // KB pointer + agent config (the setter_agent block, edited as JSON so any
  // field - persona, kb, channel, model - is reachable generically)
  const [setterState, setSetterState] = useState<SetterConfigState | null>(null);
  const [setterText, setSetterText] = useState('');

  // CORS origins (one per line)
  const [originsText, setOriginsText] = useState('');

  // cal
  const [calEmbedUrl, setCalEmbedUrl] = useState('');

  // go-live toggles
  const [sendEnabled, setSendEnabled] = useState(false);
  const [agentPaused, setAgentPaused] = useState(false);

  // secrets
  const [secrets, setSecrets] = useState<SecretStatus[]>([]);
  const [newSecretName, setNewSecretName] = useState('');
  const [newSecretValue, setNewSecretValue] = useState('');

  // public read-back (proves an edit reflected - AC3)
  const [publicConfig, setPublicConfig] = useState<PublicTenantConfig | null>(null);

  const [saving, setSaving] = useState<string | null>(null);
  const loadRequest = useRef(0);
  const activeTenant = useRef(tenantId);
  activeTenant.current = tenantId;
  const [loadedTenant, setLoadedTenant] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('identity');

  const load = useCallback(async () => {
    if (!tenantId || !user?.is_super_admin) return false;
    const request = ++loadRequest.current;
    setLoading(true);
    setError(null);
    setLoadedTenant(null);
    try {
      const [detail, branding, setter, secretList, pub] = await Promise.all([
        getTenantSetupConfig(tenantId),
        getTenantBranding(tenantId),
        getSetterConfig(tenantId),
        listTenantSecrets(tenantId),
        getPublicTenantConfig(tenantId),
      ]);
      if (request !== loadRequest.current) return false;
      if (!setter) throw new Error('Agent settings were not returned');

      setName(pick(detail, 'name') || tenantId);
      setModulesText(JSON.stringify((detail as Record<string, unknown>).modules || {}, null, 2));

      setDisplayName(pick(branding, 'display_name'));
      setLogoUrl(pick(branding, 'logo_url'));
      setPrimaryColor(pick(branding, 'primary_color'));
      setAccentColor(pick(branding, 'accent_color'));

      const integrations = (detail as Record<string, unknown>).integrations || {};
      setIntegrationsText(JSON.stringify(integrations, null, 2));
      setPixelId(pick(integrations, 'meta', 'pixel_id') || pick(pub, 'tracking', 'meta_pixel_id'));
      setGa4Id(
        pick(integrations, 'ga4', 'measurement_id') || pick(pub, 'tracking', 'ga4_measurement_id'),
      );

      setCalEmbedUrl(pick(detail, 'cal_embed_url') || pick(pub, 'cal_public_link'));

      const originList = ((detail as Record<string, unknown>).allowed_origins as string[]) || [];
      setOriginsText(Array.isArray(originList) ? originList.join('\n') : '');

      if (setter) {
        setSetterState(setter);
        setSetterText(JSON.stringify(setter.setter_agent || {}, null, 2));
        setSendEnabled(!!setter.send_enabled);
        setAgentPaused(!!setter.agent_paused);
      }
      setSecrets(secretList);
      setPublicConfig(pub);
      setLoadedTenant(tenantId);
      return true;
    } catch (e) {
      if (request === loadRequest.current) {
        setError(e instanceof Error ? e.message : 'Failed to load tenant config');
      }
      return false;
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [tenantId, user?.is_super_admin]);

  useEffect(() => {
    void load();
    return () => { loadRequest.current += 1; };
  }, [load]);

  useEffect(() => {
    setNewSecretName('');
    setNewSecretValue('');
    setActiveTab('identity');
  }, [tenantId]);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    if (loading || error || loadedTenant !== tenantId || saving) return;
    setSaving(key);
    try {
      await fn();
      if (activeTenant.current !== tenantId) return;
      if (await load()) toast.success(ok);
      else toast.warning('Saved, but settings could not be reloaded. Reload before making another change.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const parseJson = (text: string, label: string): Record<string, unknown> => {
    const trimmed = text.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(`${label} must be a JSON object`);
      }
      return parsed as Record<string, unknown>;
    } catch (e) {
      throw new Error(e instanceof Error ? `${label}: ${e.message}` : `${label} is not valid JSON`);
    }
  };

  // Super-admin gate (mirrors AgencyDashboard / VoiceAgentConfig).
  if (user && !user.is_super_admin) {
    return (
      <div className="p-8">
        <Card className="border-red-500/50 bg-red-500/10 max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="h-5 w-5" /> Super-admin access required
            </CardTitle>
            <CardDescription>The tenant setup console is restricted to super-admins.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow>Command Center</Eyebrow>
          <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Settings className="h-6 w-6" /> Setup - {tenantId}
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure this tenant end to end. Generic for any tenant; secrets are write-only.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/agency')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
        </div>
      </div>

      {isProtected && (
        <Card className="border-amber-500/50 bg-amber-500/10">
          <CardContent className="py-3 flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            <span>
              <strong>{tenantId}</strong> is a protected live tenant. Config writes are refused
              server-side (Rule 0); this console is read-only for it.
            </span>
          </CardContent>
        </Card>
      )}

      {error && <div role="alert" className="text-sm text-red-600">Settings could not be loaded: {error}. Reload to try again.</div>}
      {loading && <p role="status">Loading settings...</p>}

      {!loading && !error && loadedTenant === tenantId && <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="identity">Identity</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="tracking">Tracking</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="agent">KB + Agent</TabsTrigger>
          <TabsTrigger value="cors">CORS</TabsTrigger>
          <TabsTrigger value="secrets">Secrets</TabsTrigger>
          <TabsTrigger value="golive">Go-Live</TabsTrigger>
        </TabsList>

        {/* Identity + modules */}
        <TabsContent value="identity">
          <Card>
            <CardHeader>
              <CardTitle>Identity + modules</CardTitle>
              <CardDescription>Display name and enabled module map.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label>Display name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={isProtected} />
              </div>
              <div className="space-y-1">
                <Label>Modules (JSON object of name -&gt; bool)</Label>
                <Textarea
                  className="font-mono text-xs h-40"
                  value={modulesText}
                  onChange={(e) => setModulesText(e.target.value)}
                  disabled={isProtected}
                />
              </div>
              <Button
                disabled={isProtected || saving === 'identity'}
                onClick={() => run('identity', () => {
                  const modules = parseJson(modulesText, 'Modules') as Record<string, boolean>;
                  return saveTenantConfig(tenantId, name, modules);
                }, 'Identity saved')}
              >
                <Save className="h-4 w-4 mr-2" /> {saving === 'identity' ? 'Saving...' : 'Save identity'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
              <CardDescription>Public branding strings (no outbound URL fetch server-side).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Display name</Label>
                  <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={isProtected} />
                </div>
                <div className="space-y-1">
                  <Label>Logo URL</Label>
                  <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} disabled={isProtected} />
                </div>
                <div className="space-y-1">
                  <Label>Primary color</Label>
                  <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} disabled={isProtected} placeholder="#0F172A" />
                </div>
                <div className="space-y-1">
                  <Label>Accent color</Label>
                  <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} disabled={isProtected} placeholder="#2563EB" />
                </div>
              </div>
              <Button
                disabled={isProtected || saving === 'branding'}
                onClick={() => run('branding', () => saveTenantBranding(tenantId, {
                  display_name: displayName,
                  logo_url: logoUrl,
                  primary_color: primaryColor,
                  accent_color: accentColor,
                }), 'Branding saved')}
              >
                <Save className="h-4 w-4 mr-2" /> {saving === 'branding' ? 'Saving...' : 'Save branding'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tracking ids */}
        <TabsContent value="tracking">
          <Card>
            <CardHeader>
              <CardTitle>Tracking IDs</CardTitle>
              <CardDescription>Meta pixel + GA4 measurement id (baked into the static site at rebuild).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label>Meta pixel id</Label>
                <Input value={pixelId} onChange={(e) => setPixelId(e.target.value)} disabled={isProtected} />
              </div>
              <div className="space-y-1">
                <Label>GA4 measurement id</Label>
                <Input value={ga4Id} onChange={(e) => setGa4Id(e.target.value)} disabled={isProtected} placeholder="G-XXXXXXX" />
              </div>
              <Button
                disabled={isProtected || saving === 'tracking'}
                onClick={() => run('tracking', () => saveTenantIntegrations(tenantId, {
                  integrations: {
                    meta: { pixel_id: pixelId },
                    ga4: { measurement_id: ga4Id },
                  },
                }), 'Tracking IDs saved')}
              >
                <Save className="h-4 w-4 mr-2" /> {saving === 'tracking' ? 'Saving...' : 'Save tracking'}
              </Button>
              {publicConfig?.tracking && (
                <p className="text-xs text-muted-foreground">
                  Public read-back: pixel={publicConfig.tracking.meta_pixel_id || '(none)'} ·
                  ga4={publicConfig.tracking.ga4_measurement_id || '(none)'}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations (full block) + Cal */}
        <TabsContent value="integrations">
          <Card>
            <CardHeader>
              <CardTitle>Integrations</CardTitle>
              <CardDescription>Editable integration settings (JSON). Changes are merged; removing a field here does not delete it. Credentials and empty groups are omitted to preserve stored values.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                aria-label="Integration settings"
                className="font-mono text-xs h-56"
                value={integrationsText}
                onChange={(e) => setIntegrationsText(e.target.value)}
                disabled={isProtected}
              />
              <Button
                disabled={isProtected || saving === 'integrations'}
                onClick={() => run('integrations', () => {
                  const integrations = integrationPatch(parseJson(integrationsText, 'Integrations'));
                  if (!Object.keys(integrations).length) throw new Error('No editable integration settings to save');
                  return saveTenantIntegrations(tenantId, { integrations });
                }, 'Integrations saved')}
              >
                <Save className="h-4 w-4 mr-2" /> {saving === 'integrations' ? 'Saving...' : 'Save integrations'}
              </Button>
              <div className="pt-4 border-t space-y-1">
                <Label>Cal.com embed URL (public scheduling link)</Label>
                <Input value={calEmbedUrl} onChange={(e) => setCalEmbedUrl(e.target.value)} disabled={isProtected} />
                <Button
                  className="mt-2"
                  variant="outline"
                  disabled={isProtected || saving === 'cal'}
                  onClick={() => run('cal', () => saveTenantCalConfig(tenantId, { cal_embed_url: calEmbedUrl }), 'Cal config saved')}
                >
                  <Save className="h-4 w-4 mr-2" /> {saving === 'cal' ? 'Saving...' : 'Save Cal URL'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* KB pointer + agent config */}
        <TabsContent value="agent">
          <Card>
            <CardHeader>
              <CardTitle>KB pointer + agent config</CardTitle>
              <CardDescription>
                The setter_agent block (persona, kb pointer, channel, model). Edited as JSON
                so any field is reachable generically. send_enabled is resolved STRICTLY
                server-side (use the Go-Live tab to flip it).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                className="font-mono text-xs h-72"
                value={setterText}
                onChange={(e) => setSetterText(e.target.value)}
                disabled={isProtected}
              />
              <Button
                disabled={isProtected || saving === 'agent'}
                onClick={() => run('agent', () => {
                  const block = parseJson(setterText, 'Agent config');
                  return saveSetterConfig(tenantId, block);
                }, 'Agent config saved')}
              >
                <Save className="h-4 w-4 mr-2" /> {saving === 'agent' ? 'Saving...' : 'Save agent config'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CORS origins */}
        <TabsContent value="cors">
          <Card>
            <CardHeader>
              <CardTitle>CORS allowed origins</CardTitle>
              <CardDescription>One https origin per line (no path, no wildcard). Replaces the prior set.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                className="font-mono text-xs h-32"
                value={originsText}
                onChange={(e) => setOriginsText(e.target.value)}
                disabled={isProtected}
                placeholder="https://example.com"
              />
              <Button
                disabled={isProtected || saving === 'cors'}
                onClick={() => run('cors', () => {
                  const origins = originsText.split('\n').map((o) => o.trim()).filter(Boolean);
                  return saveTenantOrigins(tenantId, origins);
                }, 'CORS origins saved')}
              >
                <Save className="h-4 w-4 mr-2" /> {saving === 'cors' ? 'Saving...' : 'Save origins'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Secrets (R2) */}
        <TabsContent value="secrets">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Secrets</CardTitle>
              <CardDescription>
                Write-only / never-reveal. Enter or rotate a named secret; the value is
                encrypted server-side via the R2 API. The list shows set/unset + last-rotated,
                never the plaintext.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {secrets.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No managed secrets yet.</div>
                ) : (
                  secrets.map((s) => (
                    <div key={s.name || s.secret_name} className="flex items-center justify-between text-sm border rounded-md px-3 py-2">
                      <span className="font-mono">{s.name || s.secret_name}</span>
                      <div className="flex items-center gap-3">
                        <Badge variant={s.status === 'set' ? 'default' : 'secondary'}>{s.status}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {s.last_rotated_at ? `rotated ${new Date(s.last_rotated_at).toLocaleString()}` : 'never rotated'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="pt-3 border-t space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Secret name</Label>
                    <Input
                      value={newSecretName}
                      onChange={(e) => setNewSecretName(e.target.value)}
                      placeholder="e.g. ghl_api_key"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Value (write-only)</Label>
                    <Input
                      type="password"
                      value={newSecretValue}
                      onChange={(e) => setNewSecretValue(e.target.value)}
                      placeholder="never shown again"
                    />
                  </div>
                </div>
                <Button
                  disabled={!newSecretName.trim() || !newSecretValue || saving === 'secret'}
                  onClick={() => run('secret', async () => {
                    await setTenantSecret(tenantId, newSecretName.trim(), newSecretValue);
                    setNewSecretName('');
                    setNewSecretValue('');
                  }, 'Secret saved (encrypted)')}
                >
                  <KeyRound className="h-4 w-4 mr-2" /> {saving === 'secret' ? 'Saving...' : 'Set / rotate secret'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Protected tenants are refused server-side. The value is encrypted at rest and
                  never returned by any endpoint.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Go-live toggles + rebuild */}
        <TabsContent value="golive">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Power className="h-5 w-5" /> Go-live toggles</CardTitle>
              <CardDescription>
                The master enable (send_enabled) + pause (agent_paused). Default safe;
                flipping send on is an explicit operator action. The agent runs only when
                send_enabled is on AND agent_paused is off.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between border rounded-md px-3 py-2">
                <div>
                  <div className="font-medium text-sm">send_enabled (master)</div>
                  <div className="text-xs text-muted-foreground">Autonomous send. Default OFF.</div>
                </div>
                <Button
                  variant={sendEnabled ? 'default' : 'outline'}
                  size="sm"
                  disabled={isProtected}
                  onClick={() => setSendEnabled((v) => !v)}
                >
                  {sendEnabled ? 'ON' : 'OFF'}
                </Button>
              </div>
              <div className="flex items-center justify-between border rounded-md px-3 py-2">
                <div>
                  <div className="font-medium text-sm">agent_paused</div>
                  <div className="text-xs text-muted-foreground">Pause without clearing config.</div>
                </div>
                <Button
                  variant={agentPaused ? 'default' : 'outline'}
                  size="sm"
                  disabled={isProtected}
                  onClick={() => setAgentPaused((v) => !v)}
                >
                  {agentPaused ? 'PAUSED' : 'RUNNING'}
                </Button>
              </div>
              <Button
                disabled={isProtected || saving === 'golive'}
                onClick={() => run('golive', () => {
                  // Read-modify-write: preserve the rest of the setter block, flip the
                  // two toggles. The server resolves send_enabled STRICTLY.
                  const base = (setterState?.setter_agent as Record<string, unknown>) || {};
                  return saveSetterConfig(tenantId, {
                    ...base,
                    send_enabled: sendEnabled,
                    agent_paused: agentPaused,
                  });
                }, 'Go-live toggles saved')}
              >
                <Save className="h-4 w-4 mr-2" /> {saving === 'golive' ? 'Saving...' : 'Save toggles'}
              </Button>

              <div className="pt-4 border-t space-y-2">
                <CardTitle className="text-base flex items-center gap-2"><Rocket className="h-4 w-4" /> Rebuild static site</CardTitle>
                <CardDescription>
                  Dispatch a metabus-apps rebuild so a config / pixel / brand edit is
                  redeployed (edit in console -&gt; redeploy, not a source PR).
                </CardDescription>
                <Button
                  variant="outline"
                  disabled={saving === 'rebuild'}
                  onClick={() => run('rebuild', () => dispatchTenantRebuild(tenantId), 'Rebuild dispatched')}
                >
                  <Rocket className="h-4 w-4 mr-2" /> {saving === 'rebuild' ? 'Dispatching...' : 'Dispatch rebuild'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>}
    </div>
  );
}
